import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import * as api from "api/endpoints/partnerService";
import * as countryApi from "api/endpoints/country";
import * as confidenceSore from "api/endpoints/onboarding";
import { loadingPage, selectPartnersData, selectUser } from "store/slices/app";
import getLookupTree from "../utils/getLookupTree";
import { genarate_UUID } from "utils/genarateUUID";
import { loadContractTermsValues, selectContractTermsData } from "store/slices/contractTermsPage";
import downloadByUrl from "../utils/downloadByUrl";
import { formatJSONData } from "utils/formatJson";
import LookupMain from "components/BusinessLookup/LookupMain";
import matchFoundTooltipData from "../data/MarkaazMatchFoundTooltip-data.json";

export default function BusinessLookupPage() {
  const [businessMatchError, setBusinessMatchError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [lookupData, setLookupData] = useState(null);
  const [countryCode, setCountryCode] = useState([]);
  const [invocationId, setInvocationId] = useState("");
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [downloadConfirmOpen, setDownloadConfirmOpen] = useState(false);
  const [downloadInProgress, setDownloadInProgress] = useState(false);
  // const [retryData, setRetryData] = useState(null);
  const [isRetryPressed, setIsRetryPressed] = useState(false);
  const [mappingDataResult, setMappingDataResult] = useState(null);
  const [markaazConfidenceScore, setMarkaazConfidenceScore] = useState();
  const [bizMatchData, setBizmatchData] = useState();
  const [accessDeniedAlert, setAccessDeniedAlert] = useState(false);
  const [matchFoundTooltip, setMatchFoundTooltip] = useState(null);
  const navigate = useNavigate();

  const matchFoundRef = useRef(null);
  const matchErrorRef = useRef(null);
  const topSectionRef = useRef(null);
  const dispatch = useDispatch();

  const contractTermsdata = useSelector(selectContractTermsData);
  const userData = useSelector(selectUser);
  const userType = userData.userAccessRole;
  const partnersData = useSelector(selectPartnersData);
  const loadData = (userData.isSuperUser && partnersData.isPartnerChanged) || !userData.isSuperUser;

  const epId = userData.selectedPartner.id; //1

  const businessLookup = "epp_business_lookup";

  useEffect(() => {
    loadData && dispatch(loadContractTermsValues(userData.selectedPartner.id));
  }, [dispatch, userData.selectedPartner.id, loadData]);

  useEffect(() => {
    loadData &&
      confidenceSore.getMatchConfidenceSetup(userData.selectedPartner.id).then((res) => {
        setMarkaazConfidenceScore(res?.details?.matchConfidenceScoreLowerLimit);
      });
  }, [userData.selectedPartner.id, loadData]);

  useEffect(() => {
    if (userData.selectedPartner.id) {
      reset();
    }
  }, [userData.selectedPartner.id]);

  useLayoutEffect(() => {
    if (lookupData && matchFoundRef.current) {
      matchFoundRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [lookupData]);

  useLayoutEffect(() => {
    if (businessMatchError && matchErrorRef.current) {
      matchErrorRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [businessMatchError]);

  useLayoutEffect(() => {
    if (isRetryPressed) {
      topSectionRef.current.scrollIntoView({ behavior: "smooth" });
      setIsRetryPressed(false);
    }
  }, [isRetryPressed]);

  useEffect(() => {
    countryApi.getCountryCodes().then((res) => {
      setCountryCode(res?.details);
    });
  }, []);

  function handleResetClick() {
    setLookupData(null);
    setBusinessMatchError(false);
  }

  function reset() {
    setBusinessMatchError(false);
    setLookupData(null);
  }

  function handleCopyClick(name, jsonData) {
    const formattedData = formatJSONData(jsonData);
    navigator.clipboard.writeText(formattedData);
  }

  async function handleBusinessLookupSubmit(values) {
    dispatch(loadingPage(true));
    setLoading(true);
    reset();
    const invocationSource = businessLookup;
    const invocationId = genarate_UUID();
    setInvocationId(invocationId);
    const config = {
      partnerId: epId,
      matchCount: values.matchCount,
      showNonMatches: values.showNonMatches,
      showGrades: values.showGrades,
      bizInfo: values
    };
    const namesToMatch = values.names;

    api
      .postBusinessMatch(config, invocationId, invocationSource)
      .then(async (res) => {
        if (!res.markaaz.matchResults) {
          setBusinessMatchError(true);
          setBizmatchData(res);
        } else {
          await setLookupInfo(res?.markaaz, namesToMatch, invocationSource, invocationId);
        }
        setLoading(false);
        dispatch(loadingPage(false));
      })
      .catch((error) => {
        setBizmatchData(error?.response?.data);
        if (error.response.status === 403) {
          setAccessDeniedAlert(true);
        }

        setBusinessMatchError(true);
        setLoading(false);
        dispatch(loadingPage(false));
      });
  }

  async function handleDownload(format) {
    dispatch(loadingPage(true));
    setDownloadInProgress(true);
    const jsonData = [];
    let lookupDataCopy = JSON.parse(JSON.stringify(lookupData));

    lookupDataCopy.forEach((item) => {
      const markaazId = item.overview.data["Markaaz ID:"];
      const transformedData = Object.keys(item)
        .map((key) => {
          if (key === "retryData") return null;

          //TODO: Refactoring Needed
          if (format === "pdf") {
            if (key === "hierarchy" && item[key].data != null) {
              for (let i = 0; i < item[key].data["Company Hierarchy"].length; i++) {
                if ("Addresses:" in item[key].data["Company Hierarchy"][i]) {
                  item[key].data["Company Hierarchy"][i]["Addresses:"] = item[key].data["Company Hierarchy"][i][
                    "Addresses:"
                  ].map((address) => {
                    delete address["Address Type:"];
                    return address;
                  });
                }
              }
            } else if (item[key].data != null && "Addresses:" in item[key].data) {
              item[key].data["Addresses:"] = item[key].data["Addresses:"].map((address) => {
                delete address["Address Type:"];
                return address;
              });
            }
          }

          return {
            bizLookupType: key,
            json: item[key].data
          };
        })
        .filter(Boolean);

      jsonData.push({
        entityId: markaazId,
        data: transformedData
      });
    });

    const data = {
      partnerId: epId,
      entities: jsonData,
      format
    };

    const invocationSource = businessLookup;

    try {
      const response = await api.downloadReport(data, invocationId, invocationSource);
      downloadByUrl(response.details.reportUrl);
      setDownloadConfirmOpen(true);
    } catch (e) {
      console.error(e);
    }

    setDownloadOpen(false);
    setDownloadInProgress(false);
    dispatch(loadingPage(false));
  }

  function handleDownloadClose() {
    setDownloadOpen(false);
  }

  function handleDownloadOpen() {
    setDownloadOpen(true);
  }

  function handleDownloadConfirmClose() {
    setDownloadConfirmOpen(false);
  }

  function handleEditThreshold() {
    navigate("/system-level-settings/match-confidence");
  }

  async function getmappingDataResult() {
    if (mappingDataResult) return mappingDataResult;
    try {
      const lookupMappingData = await api.getLookupMapping();
      setMappingDataResult(lookupMappingData);
      return lookupMappingData;
    } catch {
      setBusinessMatchError(true);
    }
  }

  async function setLookupInfo(advanceMatchingResult, namesToMatch, invocationSource, invocationId) {
    if (!advanceMatchingResult) return;

    const results = [];

    for (const markaaz of advanceMatchingResult.matchResults) {
      const markaazId = markaaz.markaazId;

      const config = { partnerId: epId, markaazId: markaazId };
      const nameMatching = {
        partnerId: epId,
        data: {
          markaazId: markaazId,
          namesToMatch: namesToMatch
        }
      };

      try {
        let partnerDataResult = await api.getData(
          config,
          nameMatching,
          invocationId,
          invocationSource,
          contractTermsdata
        );

        partnerDataResult.bizFirmographic.bizInfo = {
          ...partnerDataResult.bizFirmographic?.bizInfo
        };

        function getMappedTooltipData(tooltipData, mappingData) {
          let mappedTooltip = {};
          for (const title in tooltipData) {
            for (const label in tooltipData[title]) {
              label in mappingData[title]
                ? (mappedTooltip[mappingData[title][label]] = tooltipData[title][label])
                : (mappedTooltip[label] = tooltipData[title][label]);
            }
          }
          return mappedTooltip;
        }

        function getObjFormPartnerDataResult(obj, ...properties) {
          for (const prop of properties) {
            const { markaazId, traceId, bizInfo } = obj?.[prop] || {};
            if (markaazId !== undefined) {
              return { markaazId, traceId, bizInfo };
            }
          }
          return {};
        }

        const PartnerDataResultObject = getObjFormPartnerDataResult(
          partnerDataResult,
          "bizFirmographic",
          "officersNameMatching",
          "bizCompliance",
          "bizHealth",
          "bizDiversity",
          "bizHierarchy"
        );
        const mappingDataResult = await getmappingDataResult();
        const markaazMatchResult = markaaz;
        const result = getLookupTree(
          mappingDataResult.details,
          partnerDataResult,
          PartnerDataResultObject,
          markaazMatchResult,
          advanceMatchingResult
        );

        const tooltip = getMappedTooltipData(matchFoundTooltipData, mappingDataResult.details);
        setMatchFoundTooltip(tooltip);

        let retryData = {};
        for (const key in partnerDataResult) {
          retryData[key] = partnerDataResult[key].showRetry;
        }

        result.retryData = retryData;

        // const updateErrorStatus = PartnerDataResultObject.markaazId || markaaz?.markaazId;
        // if (!updateErrorStatus) {
        //   setBusinessMatchError(true);
        //   return;
        // }

        // setMarkaazId(PartnerDataResultObject.markaazId || markaaz?.markaazId);

        results.push(result);
      } catch (error) {
        console.log("error:", error);
        setBusinessMatchError(false);
      }
    }

    setLookupData(results);

    console.log("setLookupData", results);
  }

  function handleRetry() {
    reset();
    setIsRetryPressed(true);
  }

  function handleAlertClose() {
    setBusinessMatchError(false);
  }

  const commonProps = {
    isPartnerChanged: userData.selectedPartner.id,
    loading: loading,
    bizMatchData: bizMatchData?.markaaz,
    contractTermsdata: contractTermsdata,
    // retryData: retryData,
    accessDeniedAlert: accessDeniedAlert,
    lookupData: lookupData,
    businessMatchError: businessMatchError,
    matchFoundRef: matchFoundRef,
    matchErrorRef: matchErrorRef,
    markaazConfidenceScore: markaazConfidenceScore,
    userType: userType,
    matchFoundTooltipData: matchFoundTooltip,
    onAlertClose: handleAlertClose,
    onRetry: handleRetry,
    onEditThreshold: handleEditThreshold
  };

  const businessLookupProps = {
    countryCode: countryCode,
    onBusinessLookupSubmit: handleBusinessLookupSubmit,
    onResetClick: handleResetClick
  };

  const downloadProps = {
    downloadOpen: downloadOpen,
    downloadConfirmOpen: downloadConfirmOpen,
    downloadInProgress: downloadInProgress,
    onDownloadOpen: handleDownloadOpen,
    onDownloadClose: handleDownloadClose,
    onDownloadConfirmClose: handleDownloadConfirmClose,
    onCopyClick: handleCopyClick,
    onDownload: handleDownload
  };
  return <LookupMain {...commonProps} {...businessLookupProps} {...downloadProps} />;
}
